import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Calendar, ChevronLeft, ChevronRight, Download, ArrowLeft,
  MapPin, Phone, Users, Loader2, Eye, Wrench, ClipboardCheck
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isToday } from 'date-fns';

const EnhancedScheduler = () => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [monthData, setMonthData] = useState([]);
  const [dayData, setDayData] = useState(null);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [loadingDay, setLoadingDay] = useState(false);

  const fetchMonthSchedule = useCallback(async () => {
    setLoadingMonth(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const role = roleFilter === 'all' ? null : roleFilter;
      const result = await api.getMonthSchedule(year, month, role);
      setMonthData(result.days || []);
    } catch (err) {
      console.error('Failed to fetch month schedule:', err);
      toast({ title: 'Error', description: 'Failed to load month schedule', variant: 'destructive' });
    } finally {
      setLoadingMonth(false);
    }
  }, [currentMonth, roleFilter]);

  const fetchDaySchedule = useCallback(async (date) => {
    setLoadingDay(true);
    try {
      const role = roleFilter === 'all' ? null : roleFilter;
      const result = await api.getDaySchedule(date, role);
      setDayData(result);
    } catch (err) {
      console.error('Failed to fetch day schedule:', err);
      toast({ title: 'Error', description: 'Failed to load day details', variant: 'destructive' });
    } finally {
      setLoadingDay(false);
    }
  }, [roleFilter]);

  useEffect(() => {
    fetchMonthSchedule();
  }, [fetchMonthSchedule]);

  useEffect(() => {
    if (selectedDate) {
      fetchDaySchedule(selectedDate);
    }
  }, [selectedDate, fetchDaySchedule]);

  const handlePrevMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
    setSelectedDate(null);
    setDayData(null);
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => addMonths(prev, 1));
    setSelectedDate(null);
    setDayData(null);
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
    setSelectedDate(null);
    setDayData(null);
  };

  const handleDateClick = (dateStr) => {
    setSelectedDate(dateStr === selectedDate ? null : dateStr);
  };

  const handleDownloadRoster = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const role = roleFilter === 'all' ? null : roleFilter;
    const url = api.getSchedulerRosterUrl(year, month, role);
    const token = localStorage.getItem('alignworks_token');
    fetch(url, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => {
        if (!res.ok) throw new Error('Download failed');
        return res.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `Roster_${format(currentMonth, 'MMMM_yyyy')}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        toast({ title: 'Downloaded', description: 'Roster CSV downloaded successfully' });
      })
      .catch(() => toast({ title: 'Error', description: 'Failed to download roster', variant: 'destructive' }));
  };

  const handleRoleChange = (value) => {
    setRoleFilter(value);
    setSelectedDate(null);
    setDayData(null);
  };

  // Calendar grid
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = getDay(monthStart);

  const dayLookup = {};
  monthData.forEach(d => { dayLookup[d.date] = d; });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Team Scheduler</h1>
              <p className="text-sm text-gray-500">Monthly calendar with downloadable roster</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={roleFilter} onValueChange={handleRoleChange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="installer">Installer</SelectItem>
                <SelectItem value="junioraligner">Junior Aligner</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleDownloadRoster}>
              <Download className="h-4 w-4 mr-2" />
              Download Roster
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={handlePrevMonth}>
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <CardTitle className="text-lg">
                      {format(currentMonth, 'MMMM yyyy')}
                    </CardTitle>
                    <Button variant="ghost" size="icon" onClick={handleNextMonth}>
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </div>
                  <Button variant="outline" size="sm" onClick={handleToday}>
                    <Calendar className="h-4 w-4 mr-1" />
                    Today
                  </Button>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span> SV1</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-purple-500"></span> SV2</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-500"></span> Installation</span>
                </div>
              </CardHeader>
              <CardContent>
                {loadingMonth ? (
                  <div className="flex items-center justify-center h-80">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : (
                  <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
                    {weekDays.map(day => (
                      <div key={day} className="bg-gray-50 text-center text-xs font-semibold text-gray-600 py-2">
                        {day}
                      </div>
                    ))}
                    {Array.from({ length: startDayOfWeek }).map((_, i) => (
                      <div key={`empty-${i}`} className="bg-white min-h-[90px]" />
                    ))}
                    {daysInMonth.map(day => {
                      const dateStr = format(day, 'yyyy-MM-dd');
                      const data = dayLookup[dateStr] || { sv1: 0, sv2: 0, installations: 0 };
                      const total = data.sv1 + data.sv2 + data.installations;
                      const isSelected = selectedDate === dateStr;
                      const today = isToday(day);

                      return (
                        <div
                          key={dateStr}
                          onClick={() => handleDateClick(dateStr)}
                          className={`bg-white min-h-[90px] p-1.5 cursor-pointer transition-all hover:bg-gray-50 relative
                          ${isSelected ? 'ring-2 ring-teal-500 ring-inset bg-teal-50' : ''}
                          ${today && !isSelected ? 'bg-blue-50/50' : ''}
                        `}
                        >
                          <div className={`text-sm font-medium mb-1 ${today ? 'bg-teal-600 text-white w-6 h-6 rounded-full flex items-center justify-center' : 'text-gray-700'}`}>
                            {format(day, 'd')}
                          </div>
                          {total > 0 && (
                            <div className="space-y-0.5">
                              {data.sv1 > 0 && (
                                <div className="flex items-center gap-1 text-[10px]">
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></span>
                                  <span className="text-blue-700 font-medium truncate">{data.sv1} SV1</span>
                                </div>
                              )}
                              {data.sv2 > 0 && (
                                <div className="flex items-center gap-1 text-[10px]">
                                  <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0"></span>
                                  <span className="text-purple-700 font-medium truncate">{data.sv2} SV2</span>
                                </div>
                              )}
                              {data.installations > 0 && (
                                <div className="flex items-center gap-1 text-[10px]">
                                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0"></span>
                                  <span className="text-orange-700 font-medium truncate">{data.installations} Install</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Day Detail Panel */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="h-5 w-5 text-gray-400" />
                  {selectedDate ? format(new Date(selectedDate + 'T00:00:00'), 'EEE, dd MMM yyyy') : 'Select a date'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedDate && (
                  <p className="text-sm text-gray-400 text-center py-8">Click on a calendar date to see the schedule</p>
                )}
                {selectedDate && loadingDay && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                )}
                {selectedDate && !loadingDay && dayData && (
                  <div className="space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto pr-1">
                    {/* SV1 */}
                    {dayData.sv1?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <ClipboardCheck className="h-4 w-4 text-blue-500" />
                          <h3 className="text-sm font-semibold text-blue-700">SV1 Schedules ({dayData.sv1.length})</h3>
                        </div>
                        <div className="space-y-2">
                          {dayData.sv1.map((item, i) => (
                            <ScheduleCard
                              key={`sv1-${i}`}
                              color="blue"
                              personName={item.supervisorName}
                              personPhone={item.supervisorPhone}
                              clientName={item.clientName}
                              address={item.siteAddress}
                              sqft={item.sqft}
                              slot={item.slot}
                              slotTime={item.slotTime}
                              status={item.status}
                              projectId={item.projectId}
                              navigate={navigate}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* SV2 */}
                    {dayData.sv2?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <ClipboardCheck className="h-4 w-4 text-purple-500" />
                          <h3 className="text-sm font-semibold text-purple-700">SV2 Schedules ({dayData.sv2.length})</h3>
                        </div>
                        <div className="space-y-2">
                          {dayData.sv2.map((item, i) => (
                            <ScheduleCard
                              key={`sv2-${i}`}
                              color="purple"
                              personName={item.supervisorName}
                              personPhone={item.supervisorPhone}
                              clientName={item.clientName}
                              address={item.siteAddress}
                              sqft={item.sqft}
                              slot={item.slot}
                              slotTime={item.slotTime}
                              status={item.status}
                              projectId={item.projectId}
                              navigate={navigate}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Installations */}
                    {dayData.installations?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <Wrench className="h-4 w-4 text-orange-500" />
                          <h3 className="text-sm font-semibold text-orange-700">Installations ({dayData.installations.length})</h3>
                        </div>
                        <div className="space-y-2">
                          {dayData.installations.map((item, i) => (
                            <InstallationCard key={`inst-${i}`} item={item} navigate={navigate} />
                          ))}
                        </div>
                      </div>
                    )}

                    {(!dayData.sv1?.length && !dayData.sv2?.length && !dayData.installations?.length) && (
                      <p className="text-sm text-gray-400 text-center py-8">No schedules for this date</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

const ScheduleCard = ({ color, personName, personPhone, clientName, address, sqft, slot, slotTime, status, projectId, navigate }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-800' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', badge: 'bg-purple-100 text-purple-800' },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div className={`${c.bg} border ${c.border} rounded-lg p-3 space-y-1.5`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">{personName}</span>
        <Badge variant="outline" className={`text-[10px] ${c.badge}`}>{status}</Badge>
      </div>
      {personPhone && (
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Phone className="h-3 w-3" /> {personPhone}
        </div>
      )}
      <div className="text-xs text-gray-700 font-medium">{clientName} &bull; {sqft} sqft</div>
      {address && (
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <MapPin className="h-3 w-3 shrink-0" /> <span className="truncate">{address}</span>
        </div>
      )}
      {slot && (
        <div className="text-xs text-gray-500">
          &#128336; {slot} {slotTime ? `(${slotTime})` : ''}
        </div>
      )}
      <Button
        variant="ghost"
        size="sm"
        className="h-6 text-xs text-teal-600 hover:text-teal-700 px-0"
        onClick={() => navigate(`/projects/${projectId}/view`)}
      >
        View Project &rarr;
      </Button>
    </div>
  );
};

const InstallationCard = ({ item, navigate }) => {
  const isInProgress = item.stageKey === 'installation_in_progress';
  return (
    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-900">{item.clientName}</span>
        <Badge variant="outline" className={isInProgress ? 'bg-amber-100 text-amber-800 text-[10px]' : 'bg-orange-100 text-orange-800 text-[10px]'}>
          {isInProgress ? 'In Progress' : 'Scheduled'}
        </Badge>
      </div>
      <div className="text-xs text-gray-700">{item.sqft} sqft {item.progress > 0 ? `\u2022 ${item.progress}% done` : ''}</div>
      {item.siteAddress && (
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <MapPin className="h-3 w-3 shrink-0" /> <span className="truncate">{item.siteAddress}</span>
        </div>
      )}
      {item.teams?.map((team, ti) => (
        <div key={ti} className="mt-1">
          <div className="flex items-center gap-1 text-xs font-medium text-gray-700">
            <Users className="h-3 w-3" /> {team.teamName}
          </div>
          <div className="ml-4 space-y-0.5">
            {team.members?.map((m, mi) => (
              <div key={mi} className="text-[11px] text-gray-600 flex items-center justify-between">
                <span>{m.name} <span className="text-gray-400">({m.role})</span></span>
                {m.phone && <span className="text-gray-400">{m.phone}</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button
        variant="ghost"
        size="sm"
        className="h-6 text-xs text-teal-600 hover:text-teal-700 px-0"
        onClick={() => navigate(`/projects/${item.projectId}/view`)}
      >
        View Project &rarr;
      </Button>
    </div>
  );
};

export default EnhancedScheduler;
